import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../../config';
import { toastSuccess, toastError, errorMessage } from '../../lib/toast';
import { formatDateTime } from '../../lib/formatDate';

const STATUS_TONE = {
  pending:  { bg: '#FEF3C7', fg: '#92400E', label: 'Pending' },
  approved: { bg: '#DCFCE7', fg: '#166534', label: 'Approved' },
  rejected: { bg: '#FEE2E2', fg: '#991B1B', label: 'Rejected' },
};

const PAGE_SIZE = 25;

/**
 * Admin queue for tool access requests.
 *
 * Triggers onCountsChange({pending}) on every refresh so the parent can
 * keep its badges accurate.
 */
export default function AdminToolAccessRequests({ onCountsChange }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolveModal, setResolveModal] = useState({ open: false, row: null, decision: 'approved', notes: '' });
  const [resolving, setResolving] = useState(false);

  const load = async (params = {}) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/tool_access_requests`, {
        params: { status, q: q.trim() || undefined, page, size: PAGE_SIZE, ...params },
        withCredentials: true,
      });
      setRows(Array.isArray(res.data.rows) ? res.data.rows : []);
      setTotal(typeof res.data.total === 'number' ? res.data.total : 0);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // Pull the pending count for badges. Independent of the visible filters.
  const refreshPendingCount = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/tool_access_requests`, {
        params: { status: 'pending', page: 1, size: 1 },
        withCredentials: true,
      });
      if (typeof res.data.total === 'number' && onCountsChange) {
        onCountsChange({ pending: res.data.total });
      }
    } catch {
      /* ignore */
    }
  };

  // `load` and `refreshPendingCount` are rebuilt every render; these effects
  // are keyed on the filters on purpose, so listing them would refetch each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, page]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshPendingCount(); }, []);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const openApprove = (row) => setResolveModal({ open: true, row, decision: 'approved', notes: '' });
  const openReject = (row) => setResolveModal({ open: true, row, decision: 'rejected', notes: '' });

  const submitResolution = async () => {
    const { row, decision, notes } = resolveModal;
    if (!row) return;
    setResolving(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/api/admin/tool_access_requests/${row.id}`,
        { status: decision, admin_notes: notes.trim() || undefined },
        { withCredentials: true }
      );
      setResolveModal({ open: false, row: null, decision: 'approved', notes: '' });
      toastSuccess(
        decision === 'approved'
          ? `Granted access — ${row.requester_name} now has ${row.tool_name}.`
          : `Rejected request from ${row.requester_name} for ${row.tool_name}.`
      );
      await load();
      await refreshPendingCount();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't resolve the request. Try again."));
    } finally {
      setResolving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <div style={{ display: 'inline-flex', gap: 4 }} role="tablist" aria-label="Status filter">
          {['pending', 'approved', 'rejected', 'all'].map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={status === k}
              onClick={() => { setStatus(k); setPage(1); }}
              style={{
                height: 30,
                padding: '0 12px',
                borderRadius: 15,
                border: status === k ? '1px solid #C0322B' : '1px solid #E8E1D4',
                background: status === k ? '#FEE2E0' : 'white',
                color: status === k ? '#C0322B' : '#0F0F10',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {k}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by requester or tool…"
          className="search-input"
          style={{ flex: 1, minWidth: 240 }}
        />
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => { setPage(1); load(); refreshPendingCount(); }}
        >
          Refresh
        </button>
      </div>

      <div className="admin-table-count" style={{ marginBottom: 8 }}>
        {loading ? 'Loading…' : `Showing ${rows.length} of ${total} request${total === 1 ? '' : 's'}`}
      </div>

      {error ? (
        <div style={{ padding: 16, background: '#FEE2E2', color: '#991B1B', borderRadius: 8 }}>{error}</div>
      ) : rows.length === 0 && !loading ? (
        <div className="admin-table-wrap" style={{ textAlign: 'center', padding: 48, color: '#718096' }}>
          {status === 'pending'
            ? 'No pending access requests. The queue is clear.'
            : 'No requests match the current filter.'}
        </div>
      ) : (
        <>
          <div className="admin-table-wrap ut-tablewrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Requester</th>
                  <th>Tool</th>
                  <th>Reason</th>
                  <th style={{ width: 140 }}>Status</th>
                  <th style={{ width: 170 }}>Submitted</th>
                  <th style={{ width: 200, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const tone = STATUS_TONE[r.status] || STATUS_TONE.pending;
                  return (
                    <tr key={r.id}>
                      <td>
                        <div className="at-primary">{r.requester_name}</div>
                        <span className="at-secondary">{r.requester_email}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {r.tool_icon_url && (
                            <img src={getImageUrl(r.tool_icon_url)} alt="" style={{ width: 24, height: 24, objectFit: 'contain', borderRadius: 4 }} />
                          )}
                          <span className="at-primary">{r.tool_name}</span>
                        </div>
                      </td>
                      <td className="at-muted" style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                        {r.reason}
                        {r.admin_notes && (
                          <div style={{ marginTop: 4, color: '#991B1B', fontSize: 12 }}>
                            <strong>Admin:</strong> {r.admin_notes}
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            padding: '3px 10px',
                            borderRadius: 999,
                            background: tone.bg,
                            color: tone.fg,
                          }}
                        >
                          {tone.label}
                        </span>
                        {r.auto_approved && (
                          <div style={{ marginTop: 2, fontSize: 11, color: '#64748B' }}>auto-approved</div>
                        )}
                      </td>
                      <td className="at-muted">
                        {formatDateTime(r.created_at)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {r.status === 'pending' ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-small btn-primary"
                              onClick={() => openApprove(r)}
                              style={{ marginRight: 6 }}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="btn btn-small btn-secondary"
                              onClick={() => openReject(r)}
                            >
                              Reject
                            </button>
                          </>
                        ) : r.resolved_by_name ? (
                          <span style={{ fontSize: 12, color: '#64748B' }}>
                            by {r.resolved_by_name}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="admin-table-footer">
              <span>Page {page} of {totalPages}</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  style={{ padding: '6px 12px', fontSize: 13 }}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  style={{ padding: '6px 12px', fontSize: 13 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {resolveModal.open && (
        <ResolveModal
          row={resolveModal.row}
          decision={resolveModal.decision}
          notes={resolveModal.notes}
          submitting={resolving}
          onNotesChange={(notes) => setResolveModal((m) => ({ ...m, notes }))}
          onCancel={() => !resolving && setResolveModal({ open: false, row: null, decision: 'approved', notes: '' })}
          onSubmit={submitResolution}
        />
      )}
    </div>
  );
}

function ResolveModal({ row, decision, notes, submitting, onNotesChange, onCancel, onSubmit }) {
  const isApprove = decision === 'approved';
  return (
    <div className="modal-overlay" onClick={submitting ? undefined : onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>
          {isApprove ? 'Approve' : 'Reject'} access request
        </h2>
        <p style={{ color: '#475569', marginBottom: 12 }}>
          {isApprove
            ? <>Grant <strong>{row.requester_name}</strong> access to <strong>{row.tool_name}</strong>?</>
            : <>Reject <strong>{row.requester_name}</strong>'s request for <strong>{row.tool_name}</strong>?</>}
        </p>
        <div className="form-group">
          <label htmlFor="resolve-notes">
            Admin notes {isApprove ? '(optional)' : '(strongly recommended)'}
          </label>
          <textarea
            id="resolve-notes"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            rows={3}
            placeholder={isApprove
              ? 'Add a note the requester will see (optional).'
              : 'Tell the requester why so they can adjust and resubmit if needed.'}
            style={{ width: '100%', resize: 'vertical', minHeight: 80 }}
            maxLength={2000}
          />
        </div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className={`btn ${isApprove ? 'btn-primary' : 'btn-danger'}`}
            onClick={onSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : isApprove ? 'Approve & grant' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}
